import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { MultiSelect } from "primereact/multiselect";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { fetchData, postData } from "../../../Services/GlobalService";
import * as Fa from "react-icons/fa";
import type {
  ConstructFormData,
  ConstructType,
  KpiSegmentMapping,
} from "./Construct";
import { downloadExcelDateFormat } from "../../../Services/GlobalFunction/DownLoadExcel";
import { toast } from "react-toastify";
import Loader from "../../../Components/Loader";
import { AxiosExcel } from "../../../API/Axios";
import { ExtractExcelFileData1 } from "../../../Services/GlobalFunction/ExcelFileData";

const Controller = "ConstructDBSIP";

const DbSipEndPoint = {
  GetStreamAndChannel: "GetStreamAndChannel",
  SaveConstructSegMapping: "SaveConstructSegMapping",
  GetAllSegData: "GetAllSegData",
  GetSegmentMapData: "GetSegmentMapData",
  SaveStreamChannel: "SaveStreamChannel",
  DownloadSegmentTemplate: "DownloadSegmentTemplate",
  GetAllMappedDataDownload: "GetAllMappedDataDownload",
  SegmentCodeExcelUpload: "SegmentCodeExcelUpload",
};

const SPECIAL_PARAM_ID = 4;

interface Props {
  formData: ConstructFormData;
  setFormData: React.Dispatch<React.SetStateAction<ConstructFormData>>;
  constructType: ConstructType;
  constructSeq: number | null;
}

interface SelectOption {
  label: string;
  value: string;
}

const sectionLabelStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 600,
  fontSize: "0.88rem",
  color: "#1a7a3c",
  marginBottom: "8px",
};

const stepBadge: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#1a7a3c",
  color: "#fff",
  borderRadius: "50%",
  width: "20px",
  height: "20px",
  fontSize: "0.72rem",
  fontWeight: 700,
  flexShrink: 0,
};

const radioLabel: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  cursor: "pointer",
  fontSize: "0.88rem",
  fontWeight: 500,
  color: "#333",
};

const pillBtn = (bg: string, disabled = false): React.CSSProperties => ({
  backgroundColor: disabled ? "#bbb" : bg,
  color: "#fff",
  border: "none",
  borderRadius: "20px",
  padding: "7px 20px",
  fontWeight: 700,
  fontSize: "0.82rem",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
  boxShadow: disabled ? "none" : "0 2px 6px rgba(0,0,0,0.22)",
  whiteSpace: "nowrap" as const,
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
});

export default function SegmentMapTab({
  formData,
  setFormData,
  constructType,
  constructSeq,
}: Props) {
  const [searchParams] = useSearchParams();

  const seqFromUrl = searchParams.get("Construct_Seq");
  const effectiveSeq = constructSeq ?? (seqFromUrl ? Number(seqFromUrl) : null);

  const sectionKey =
    constructType === "B2C"
      ? "b2c"
      : constructType === "HDSSD"
        ? "hsssd"
        : "industrial";

  const mappings: KpiSegmentMapping[] =
    formData.segmentMap[sectionKey].mappings;

  const [activeMapping, setActiveMapping] = useState<KpiSegmentMapping | null>(
    null,
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  // Standard (non-special) segment/stream state
  const [segmentOptions, setSegmentOptions] = useState<SelectOption[]>([]);
  const [draftSegments, setDraftSegments] = useState<string[]>([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [streamOptions, setStreamOptions] = useState<SelectOption[]>([]);
  const [draftStreams, setDraftStreams] = useState<string[]>([]);
  const [loadingStreams, setLoadingStreams] = useState(false);

  // Special param (paramId === 4) state
  const [specialMode, setSpecialMode] = useState<"upload" | "distributor">(
    "distributor",
  );
  const [distributorOptions, setDistributorOptions] = useState<SelectOption[]>(
    [],
  );
  const [loadingDistributors, setLoadingDistributors] = useState(false);
  const [draftDistributors, setDraftDistributors] = useState<string[]>([]);
  const [preselectedDistributorCodes, setPreselectedDistributorCodes] =
    useState<string[]>([]);

  // Upload state
  const [uploadFileName, setUploadFileName] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "saved"
  >("idle");
  const [uploadError, setUploadError] = useState("");
  const [uploadErrorRows, setUploadErrorRows] = useState<
    Record<string, unknown>[] | null
  >(null);

  const [isBusy, setIsBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const UserId = localStorage.getItem("UserId")?.toString();

  // ─── Load helpers ────────────────────────────────────────────────────────────

  const loadSegmentOptions = async (mapping: KpiSegmentMapping) => {
    setLoadingSegments(true);
    setSegmentOptions([]);
    setStreamOptions([]);
    try {
      const res = await fetchData(
        Controller,
        DbSipEndPoint.GetStreamAndChannel,
        `FLAG=3&CONSTRUCT_TYPE=${constructType}&CONSTRUCT_SEQ=${effectiveSeq ?? ""}&PARAM_SEQ=${mapping.paramId}&STREAM_CODE=null`,
      );
      const data = res?.Data ?? [];
      const options: SelectOption[] = data.map((d: any) => ({
        label: `${d.StreamCode ?? d.label ?? String(d)}-${d.StreamDesc ?? d.value ?? String(d)}`,
        value: d.StreamCode ?? d.value ?? String(d),
      }));
      setSegmentOptions(options);
      const defaultSelected = data
        .filter((d: any) => d.Status === "Exists")
        .map((d: any) => d.StreamCode ?? d.value);
      if (defaultSelected.length > 0) {
        setDraftSegments([...defaultSelected]);
        await loadStreamOptions(mapping, defaultSelected);
      } else {
        setDraftSegments([]);
        setStreamOptions([]);
      }
    } catch {
      setSegmentOptions([]);
      setDraftSegments([]);
    } finally {
      setLoadingSegments(false);
    }
  };

  const loadStreamOptions = async (
    mapping: KpiSegmentMapping,
    selectedSegments: string[],
  ) => {
    if (selectedSegments.length === 0) {
      setStreamOptions([]);
      setDraftStreams([]);
      return;
    }
    setLoadingStreams(true);
    setStreamOptions([]);
    try {
      const res = await fetchData(
        Controller,
        DbSipEndPoint.GetStreamAndChannel,
        `FLAG=4&CONSTRUCT_TYPE=${constructType}&CONSTRUCT_SEQ=${effectiveSeq ?? ""}&PARAM_SEQ=${mapping.paramId}&STREAM_CODE=${selectedSegments.join(",")}`,
      );
      const data = res?.Data ?? [];
      const options: SelectOption[] = data.map((d: any) => ({
        label: `${d.ChannelCode}-${d.ChannelDesc}`,
        value: d.ChannelCode,
      }));
      setStreamOptions(options);
      const defaultSelected = data
        .filter((d: any) => d.Status?.toLowerCase() === "exists")
        .map((d: any) => d.ChannelCode);
      setDraftStreams(defaultSelected.length > 0 ? [...defaultSelected] : []);
    } catch {
      setStreamOptions([]);
      setDraftStreams([]);
    } finally {
      setLoadingStreams(false);
    }
  };

  const loadDistributorOptions = async () => {
    setLoadingDistributors(true);
    try {
      const res = await fetchData(
        Controller,
        DbSipEndPoint.GetAllSegData,
        `isTemplateyes=N`,
      );
      const data = res?.Data ?? [];
      const options: SelectOption[] = data.map((d: any) => ({
        label: `${d.SEG_CODE}-${d.SEG_DESC}`,
        value: d.SEG_CODE,
      }));
      setDistributorOptions(options);
      if (preselectedDistributorCodes.length > 0) {
        const matched = options
          .filter((opt) => preselectedDistributorCodes.includes(opt.value))
          .map((opt) => opt.value);
        setDraftDistributors(matched);
      }
    } catch {
      setDistributorOptions([]);
      setDraftDistributors([]);
    } finally {
      setLoadingDistributors(false);
    }
  };

  // ─── Preselect on load ────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchPreselectedData = async () => {
      if (constructType !== "B2C" || !effectiveSeq) return;
      try {
        const res = await fetchData(
          Controller,
          DbSipEndPoint.GetSegmentMapData,
          `CONSTRUCT_ID=${effectiveSeq}`,
        );
        const table1 = res?.Data?.Table1 ?? [];
        setPreselectedDistributorCodes(
          table1.map((item: any) => item.SegmentCode),
        );
      } catch {
        setPreselectedDistributorCodes([]);
      }
    };
    fetchPreselectedData();
  }, [constructType, effectiveSeq]);

  // ─── Save helpers ─────────────────────────────────────────────────────────────

  const saveStandardSegmentMap = async (
    mapping: KpiSegmentMapping,
    channels: string[],
  ): Promise<boolean> => {
    try {
      const payload = {
        pvc_Stream: constructType,
        constructseq: effectiveSeq?.toString(),
        parameterseq: mapping.paramId.toString(),
        channelCode: channels.join(","),
      };
      await postData(Controller, DbSipEndPoint.SaveStreamChannel, payload);
      return true;
    } catch (err) {
      console.error(
        "[SegmentMap] SaveStreamChannel failed for PARAM_SEQ",
        mapping.paramId,
        err,
      );
      return false;
    }
  };

  const saveSpecialSegmentMap = async (
    mapping: KpiSegmentMapping,
    segmentCodes: string[],
    status: "EA" | "A",
  ): Promise<{
    success: boolean;
    errorRows: Record<string, unknown>[] | null;
  }> => {
    try {
      const lstConsSegMap = segmentCodes.map((code) => ({
        pvc_ParameterSeq: String(mapping.paramId),
        pvC_SegmentCode: code,
        pvC_STATUS: status,
      }));

      const payload = {
        pvc_ConstructSeq: String(effectiveSeq),
        pvc_Stream: constructType,
        pvc_UserID: UserId,
        lstConsSegMap,
      };

      const res = await postData(
        Controller,
        DbSipEndPoint.SaveConstructSegMapping,
        payload,
      );
      const errorRows: Record<string, unknown>[] | null =
        res?.ErrorRows?.length > 0 ? res.ErrorRows : null;
      return { success: !errorRows, errorRows };
    } catch (err) {
      console.error("[SegmentMap] SaveConstructSegMapping failed:", err);
      return { success: false, errorRows: null };
    }
  };

  // ─── Dialog open/close ────────────────────────────────────────────────────────

  const openMap = async (mapping: KpiSegmentMapping) => {
    const m = mapping as any;
    setActiveMapping(mapping);
    setDraftSegments([...mapping.streams]);
    setDraftStreams([...mapping.channels]);
    setSpecialMode("distributor");
    setUploadFileName(m.fileName ?? "");
    setUploadFile(null);
    setUploadStatus(m.uploaded ? "saved" : "idle");
    setUploadError("");
    setUploadErrorRows(null);
    setIsBusy(false);
    setDraftDistributors(m.distributors ?? []);
    setDialogOpen(true);

    if (mapping.paramId === SPECIAL_PARAM_ID) {
      loadDistributorOptions();
    } else {
      await loadSegmentOptions(mapping);
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setActiveMapping(null);
  };

  // ─── Standard dialog handlers ─────────────────────────────────────────────────

  const handleSegmentChange = async (selected: string[]) => {
    setDraftSegments(selected);
    setDraftStreams([]);
    if (activeMapping) await loadStreamOptions(activeMapping, selected);
  };

  // ─── Upload handlers ──────────────────────────────────────────────────────────

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isXlsx =
      file.name.endsWith(".xlsx") ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    if (!isXlsx) {
      setUploadError("Please select a valid .xlsx file.");
      e.target.value = "";
      return;
    }
    setUploadFile(file);
    setUploadFileName(file.name);
    setUploadError("");
    setUploadErrorRows(null);
    setUploadStatus("idle");
    // reset input so same file can be re-selected if needed
    e.target.value = "";
  };

  const handleUploadClick = async () => {
    if (!uploadFile) {
      fileInputRef.current?.click();
      return;
    }
    if (!activeMapping) return;

    setIsBusy(true);
    setUploadError("");
    setUploadErrorRows(null);
    setUploadStatus("uploading");

    console.log("uploadFile", uploadFile);
    const result = await ExtractExcelFileData1(uploadFile);
    console.log("result", result);
    if (result.data.length === 0 || result.data.length === null) {
    }

    try {
      const formData = new FormData();
      formData.append("FileContent", uploadFile);

      // Call SegmentCodeExcelUpload — validates the file and returns segment codes or errors
      const res = await AxiosExcel.post(
        `${Controller}/SegmentCodeExcelUpload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (res?.data?.status === "Warning") {
        setUploadError(
          res.data.message ||
            "File validation warning. Please check the file and try again.",
        );
        setUploadStatus("idle");
        setIsBusy(false);
        return;
      }

      // API returns error rows when file has invalid data
      if (res?.data?.status === "error") {
        if (res?.data?.Data.length > 0) {
          setUploadErrorRows(res.data.Data);
          setUploadError(
            `File validation failed with ${res.data.Data.length} error${res.data.Data.length !== 1 ? "s" : ""}. Download the error file to review.`,
          );
          setUploadStatus("idle");
          setIsBusy(false);
          return;
        }
      }

      // File is valid — extract segment codes returned by the API
      const segmentCodes: string[] = (result?.data ?? [])
        .map((row: any) =>
          String(row["Segment Code"] ?? ""),
        )
        .filter(Boolean);

      if (segmentCodes.length === 0) {
        setUploadError("No valid segment codes found in the uploaded file.");
        setUploadStatus("idle");
        setIsBusy(false);
        return;
      }

      console.log("segmentCodes",segmentCodes);

      // Save with pvC_STATUS: "EA" for Excel-uploaded data
      const { success, errorRows } = await saveSpecialSegmentMap(
        activeMapping,
        segmentCodes,
        "EA",
      );

      if (errorRows && errorRows.length > 0) {
        setUploadErrorRows(errorRows);
        setUploadError(
          `Save completed with ${errorRows.length} error${errorRows.length !== 1 ? "s" : ""}. Download the error file to review.`,
        );
        setUploadStatus("idle");
        setIsBusy(false);
        return;
      }

      if (!success) {
        setUploadError("Save failed. Please try again.");
        setUploadStatus("idle");
        setIsBusy(false);
        return;
      }

      setUploadStatus("saved");
      persistMapping(activeMapping.kpiId, [], [], uploadFileName, [], true);
      toast.success("Segment codes uploaded and saved successfully.");
    } catch (err) {
      console.error("[SegmentMap] Excel upload failed:", err);
      setUploadError("Upload failed. Please try again.");
      setUploadStatus("idle");
    } finally {
      setIsBusy(false);
    }
  };

  const handleDownloadErrorFile = () => {
    if (!uploadErrorRows || uploadErrorRows.length === 0) return;
    downloadExcelDateFormat(
      uploadErrorRows,
      `SegmentMap_ErrorFile_${Date.now()}.xlsx`,
    );
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await fetchData(
        Controller,
        DbSipEndPoint.GetAllSegData,
        `isTemplateyes=Y`,
      );
      const data = res?.Data ?? [];
      if (data.length > 0) {
        downloadExcelDateFormat(data, `SegmentMap_Template.xlsx`);
      } else {
        toast.warn("No template data available for download.");
      }
    } catch {
      toast.warn("Failed to download template. Please try again.");
    }
  };

  // ─── Ok handler ───────────────────────────────────────────────────────────────

  const handleOk = async () => {
    if (!activeMapping) return;

    if (activeMapping.paramId === SPECIAL_PARAM_ID) {
      if (specialMode === "upload") {
        if (uploadStatus !== "saved") {
          setUploadError(
            "Please upload and confirm the file before clicking Ok.",
          );
          return;
        }
        setDialogOpen(false);
        setActiveMapping(null);
        return;
      }

      // Distributor (multiselect) mode — save with pvC_STATUS: "A"
      if (draftDistributors.length === 0) {
        setUploadError("Please select at least one segment.");
        return;
      }
      setIsBusy(true);
      setUploadError("");
      const { success, errorRows } = await saveSpecialSegmentMap(
        activeMapping,
        draftDistributors,
        "A",
      );
      setIsBusy(false);

      if (errorRows && errorRows.length > 0) {
        setUploadErrorRows(errorRows);
        setUploadError(
          `Save completed with ${errorRows.length} error${errorRows.length !== 1 ? "s" : ""}. Download the error file to review.`,
        );
        return;
      }
      if (!success) {
        setUploadError("Save failed. Please try again.");
        return;
      }
      persistMapping(activeMapping.kpiId, [], [], "", draftDistributors, true);
    } else {
      // Standard segment/stream flow
      setIsBusy(true);
      setUploadError("");
      const success = await saveStandardSegmentMap(activeMapping, draftStreams);
      setIsBusy(false);
      if (!success) {
        setUploadError("Save failed. Please try again.");
        return;
      }
      persistMapping(
        activeMapping.kpiId,
        draftSegments,
        draftStreams,
        "",
        [],
        true,
      );
    }

    setDialogOpen(false);
    setActiveMapping(null);
  };

  // ─── Persist to formData ──────────────────────────────────────────────────────

  const persistMapping = (
    kpiId: string,
    streams: string[],
    channels: string[],
    fileName: string,
    distributors: string[],
    uploaded: boolean,
  ) => {
    setFormData((prev) => ({
      ...prev,
      segmentMap: {
        ...prev.segmentMap,
        [sectionKey]: {
          mappings: prev.segmentMap[sectionKey].mappings.map((m) =>
            m.kpiId === kpiId
              ? ({
                  ...m,
                  streams,
                  channels,
                  fileName,
                  distributors,
                  uploaded,
                } as any)
              : m,
          ),
        },
      },
    }));
  };

  // ─── Download mapped segments ─────────────────────────────────────────────────

  const handleDwndmappedSeg = async () => {
    try {
      const res = await fetchData(
        Controller,
        DbSipEndPoint.GetAllMappedDataDownload,
        `CONSTRUCT_SEQ=${effectiveSeq ?? ""}&STREAM_CODE=${constructType}`,
      );
      const datadownload = res?.Data ?? [];
      if (datadownload.length > 0) {
        downloadExcelDateFormat(
          datadownload,
          `SegmentMap_MappedData_${Date.now()}.xlsx`,
        );
      } else {
        toast.warn("No mapped data available for download.");
      }
    } catch (error: any) {
      console.error("Download failed:", error);
    }
  };

  // ─── Upload button label ──────────────────────────────────────────────────────

  const uploadBtnLabel = () => {
    if (uploadStatus === "uploading") return "Uploading…";
    if (uploadStatus === "saved") return "Uploaded";
    if (!uploadFile) return "Select";
    return "Upload";
  };

  const uploadBtnColor = uploadStatus === "saved" ? "#2e8b57" : "#1a7a3c";

  // ─── Renderers ────────────────────────────────────────────────────────────────

  const renderStandardDialog = () => (
    <>
      <div style={{ marginBottom: "20px" }}>
        <div style={sectionLabelStyle}>
          <span style={stepBadge}>1</span>
          Choose Segment
        </div>
        {loadingSegments ? (
          <Loader />
        ) : (
          <MultiSelect
            value={draftSegments}
            options={segmentOptions}
            onChange={(e) => handleSegmentChange(e.value)}
            placeholder="Select Segments"
            display="chip"
            style={{ width: "100%" }}
            showSelectAll
            filter
            panelStyle={{ fontSize: "0.85rem" }}
            selectedItemsLabel="{0} segments selected"
            emptyFilterMessage="No segments found"
          />
        )}
      </div>

      <div style={{ marginBottom: "8px" }}>
        <div style={sectionLabelStyle}>
          <span style={stepBadge}>2</span>
          Choose Stream
          {draftSegments.length === 0 && (
            <span
              style={{
                fontSize: "0.75rem",
                color: "#999",
                marginLeft: 8,
                fontWeight: 400,
              }}
            >
              (select a segment first)
            </span>
          )}
        </div>
        {loadingStreams ? (
          <Loader />
        ) : (
          <MultiSelect
            value={draftStreams}
            options={streamOptions}
            onChange={(e) => setDraftStreams(e.value)}
            placeholder={
              draftSegments.length === 0
                ? "— select a segment first —"
                : "Select Streams"
            }
            display="chip"
            disabled={draftSegments.length === 0 || loadingStreams}
            style={{ width: "100%" }}
            showSelectAll
            filter
            panelStyle={{ fontSize: "0.85rem" }}
            selectedItemsLabel="{0} streams selected"
            emptyFilterMessage="No streams found"
          />
        )}
      </div>
    </>
  );

  const renderSpecialDialog = () => (
    <>
      {/* Radio toggle */}
      <div style={{ marginBottom: "20px" }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: "0.88rem",
            color: "#333",
            marginBottom: "10px",
          }}
        >
          Choose one method to select Segment
        </div>
        <div style={{ display: "flex", gap: "28px" }}>
          <label style={radioLabel}>
            <input
              type="radio"
              name="specialMode"
              checked={specialMode === "distributor"}
              onChange={() => {
                setSpecialMode("distributor");
                setUploadError("");
                setUploadErrorRows(null);
              }}
              style={{ accentColor: "#1a7a3c" }}
            />
            Select Segment
          </label>
          <label style={radioLabel}>
            <input
              type="radio"
              name="specialMode"
              checked={specialMode === "upload"}
              onChange={() => {
                setSpecialMode("upload");
                setUploadError("");
                setUploadErrorRows(null);
              }}
              style={{ accentColor: "#1a7a3c" }}
            />
            Excel Upload
          </label>
        </div>
      </div>

      {specialMode === "upload" ? (
        /* ── UPLOAD MODE ── */
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div
            style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}
          >
            {/* Label */}
            <div
              style={{
                fontWeight: 600,
                fontSize: "0.85rem",
                color: "#333",
                minWidth: "56px",
                paddingTop: "7px",
                lineHeight: "1.3",
              }}
            >
              Upload File
            </div>

            {/* Controls */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                flex: 1,
              }}
            >
              {/* File-picker row */}
              <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                <input
                  type="text"
                  readOnly
                  value={uploadFileName}
                  placeholder="No file selected"
                  style={{
                    width: "200px",
                    padding: "6px 10px",
                    border: "1px solid #bbb",
                    borderRight: "none",
                    fontSize: "0.83rem",
                    outline: "none",
                    backgroundColor: "#fff",
                    color: uploadFileName ? "#333" : "#999",
                    borderRadius: "3px 0 0 3px",
                  }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isBusy}
                  style={{
                    padding: "6px 16px",
                    fontSize: "0.83rem",
                    border: "1px solid #bbb",
                    borderRight: "none",
                    backgroundColor: "#f0f0f0",
                    cursor: isBusy ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    color: "#333",
                    whiteSpace: "nowrap",
                    borderRadius: 0,
                  }}
                >
                  Select
                </button>

                {/* Upload button */}
                <button
                  onClick={handleUploadClick}
                  disabled={isBusy || !uploadFile}
                  style={{
                    ...pillBtn(
                      uploadBtnColor,
                      isBusy || !uploadFile,
                    ),
                    borderRadius: "0 4px 4px 0",
                    padding: "7px 16px",
                    fontSize: "0.82rem",
                  }}
                >
                  {uploadBtnLabel()}
                </button>

                {/* Segment Code Template Download */}
                <button
                  onClick={handleDownloadTemplate}
                  className="btn anime-btn"
                  title="Download segment code template"
                  style={{ marginLeft: "8px" }}
                >
                  Segment Code Download
                </button>

                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>

              {/* Hint when no file selected */}
              {!uploadFileName && uploadStatus === "idle" && (
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#888",
                    fontStyle: "italic",
                  }}
                >
                  (Select an .xlsx file to upload)
                </div>
              )}

              {/* Error message */}
              {uploadError && (
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#c0392b",
                    fontWeight: 500,
                  }}
                >
                  {uploadError}
                </div>
              )}

              {/* Download Error File — only shown when there are error rows */}
              {uploadErrorRows && uploadErrorRows.length > 0 && (
                <div style={{ marginTop: "4px" }}>
                  <button
                    onClick={handleDownloadErrorFile}
                    className="btn anime-red-btn"
                    title="Download rows that failed validation"
                  >
                    Download Error File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ── SELECT SEGMENT (MULTISELECT) MODE ── */
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={sectionLabelStyle}>
            <span style={stepBadge}>→</span>
            Select Segment
          </div>

          {loadingDistributors ? (
            <Loader />
          ) : (
            <MultiSelect
              value={draftDistributors}
              options={distributorOptions}
              onChange={(e) => setDraftDistributors(e.value)}
              placeholder="Select Segments"
              display="chip"
              style={{ width: "100%" }}
              showSelectAll
              filter
              panelStyle={{ fontSize: "0.85rem" }}
              selectedItemsLabel="{0} Segments selected"
              emptyFilterMessage="No Segments found"
            />
          )}

          {uploadError && (
            <div style={{ fontSize: "0.78rem", color: "#c0392b" }}>
              {uploadError}
            </div>
          )}

          {uploadErrorRows && uploadErrorRows.length > 0 && (
            <button
              onClick={handleDownloadErrorFile}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 18px",
                fontSize: "0.82rem",
                border: "none",
                backgroundColor: "#b71c1c",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 700,
                borderRadius: "20px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                alignSelf: "flex-start",
              }}
            >
              Download Error File
            </button>
          )}
        </div>
      )}
    </>
  );

  const isLoading = loadingSegments || loadingStreams || loadingDistributors;

  return (
    <div style={{ padding: "4px 0" }}>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "14px",
        }}
      >
        <button onClick={handleDwndmappedSeg} className="btn anime-btn">
          Download Mapped Segment <Fa.FaDownload className="ms-2" />
        </button>
      </div>

      {/* Mappings table */}
      <div className="DataTable">
        <DataTable value={mappings} stripedRows style={{ width: "100%" }}>
          <Column
            field="parameter"
            header="Parameter"
            style={{ width: "45%" }}
          />
          <Column
            field="applicableOn"
            header="Applicable On"
            style={{ width: "25%" }}
            body={(rowData) => <div>{rowData.applicableOn}</div>}
          />
          <Column
            header="Actions"
            style={{ width: "15%", textAlign: "center" }}
            body={(rowData) => (
              <span
                onClick={() => openMap(rowData)}
                style={{
                  color: "#c0392b",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  cursor: "pointer",
                  textDecoration: "underline",
                  letterSpacing: "0.02em",
                }}
              >
                Map
              </span>
            )}
          />
        </DataTable>
      </div>

      {/* Dialog */}
      <Dialog
        visible={dialogOpen}
        onHide={handleCancel}
        header={
          <p className="mb-0">
            {activeMapping?.paramId === SPECIAL_PARAM_ID
              ? "Distribution Mapping"
              : "Segment & Stream Mapping"}
          </p>
        }
        style={{ width: "720px" }}
        modal
        draggable={false}
        resizable={false}
        closable={false}
        contentStyle={{ padding: "20px 24px 10px" }}
        headerStyle={{ borderBottom: "1px solid #eee", paddingBottom: "12px" }}
      >
        {activeMapping && (
          <div
            style={{
              marginBottom: "16px",
              padding: "8px 12px",
              background: "#f5f5f5",
              borderRadius: "4px",
              fontSize: "0.82rem",
              color: "#333",
            }}
          >
            <strong>Parameter:</strong> {activeMapping.parameter}
          </div>
        )}

        {activeMapping?.paramId === SPECIAL_PARAM_ID
          ? renderSpecialDialog()
          : renderStandardDialog()}

        <div
          style={{
            display: "flex",
            justifyContent: "end",
            gap: "12px",
            paddingTop: "16px",
            paddingBottom: "8px",
            borderTop: "1px solid #eee",
            marginTop: "16px",
          }}
        >
          <button
            onClick={handleOk}
            disabled={isLoading || isBusy}
            className="btn anime-btn"
          >
            Ok
          </button>
          <button onClick={handleCancel} className="btn anime-red-btn">
            Cancel
          </button>
        </div>
      </Dialog>
    </div>
  );
}
